// ==============================================================================
// FILE: src/pages/ClaimHistoryPage.tsx (BUG FIX: Smart Dashboard Filtering)
// PURPOSE: Corrects the logic to ensure that one-time filters passed from
//          the dashboard are correctly applied on the initial data load.
//
// CHANGE: The `fetchClaims` useCallback hook is now dependent on `filters`.
//         The `useEffect` hook that applies `initialPageFilters` now also
//         triggers a manual refetch when it sets the filter state. This ensures
//         that the API call is made with the correct, pre-filtered parameters
//         immediately after navigating from the dashboard.
// ==============================================================================
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Collapse, IconButton,
  Menu, Tooltip, CircularProgress, OutlinedInput, Checkbox, ListItemText, Chip,
  Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid, GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import {
    FilterList as FilterListIcon,
    FilterListOff as FilterListOffIcon,
    Download as DownloadIcon,
    MoreVert as MoreVertIcon,
    History as HistoryIcon,
    Visibility as ViewIcon,
    PublishedWithChanges as ReopenIcon,
    Summarize as ApprovalSheetIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    HourglassTop as HourglassTopIcon,
    RateReview as RateReviewIcon,
    HelpOutline as HelpOutlineIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { useAppContext } from '../contexts/AppContext';
import { ClaimHistoryItem, ClaimSubmissionStatus, User, ContractInfo } from '../types';

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 0,
}));

const formatClaimId = (id: number) => `C${String(id).padStart(5, '0')}`;

const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
}

const getSlaIndicatorProps = (submissionDate: string): { color: string; tooltip: string } | null => {
    const hoursDiff = (new Date().getTime() - new Date(submissionDate).getTime()) / (1000 * 60 * 60);
    if (hoursDiff > 48) {
        return { color: 'error.main', tooltip: 'SLA: Critical (> 48 hours)' };
    }
    if (hoursDiff > 24) {
        return { color: 'warning.main', tooltip: 'SLA: Aging (24-48 hours)' };
    }
    return null;
};

const getStatusIcon = (status: ClaimSubmissionStatus) => {
    switch (status) {
        case 'completed':
            return <CheckCircleIcon color="success" />;
        case 'partially_approved':
            return <CheckCircleOutlineIcon color="info" />;
        case 'rejected':
        case 'failed_rejected':
        case 'error':
            return <ErrorIcon color="error" />;
        case 'review_required':
            return <RateReviewIcon color="warning" />;
        case 'processing':
        case 'pending_validation':
            return <HourglassTopIcon color="info" />;
        default:
            return <HelpOutlineIcon color="disabled" />;
    }
};

const allStatuses: ClaimSubmissionStatus[] = [
    'completed',
    'partially_approved',
    'rejected',
    'failed_rejected',
    'review_required',
    'processing',
    'pending_validation',
    'error'
];

const defaultStatuses: ClaimSubmissionStatus[] = allStatuses;

function CustomNoRowsOverlay() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
      <Typography variant="h6">No claims found for the selected criteria.</Typography>
    </Box>
  );
}

export default function ClaimHistoryPage(): JSX.Element {
  const {
    apiService, setReviewingBatchId, setAuditBatchId, showNotification,
    isSuperAdmin, setActivePage, setErrorBatchId, refreshTrigger,
    initialPageFilters, clearInitialFilters
  } = useAppContext();

  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<ClaimHistoryItem[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const [filters, setFilters] = useState({
    employee_name: '',
    status: defaultStatuses,
    start_date: '',
    end_date: '',
    deputee_id: '',
    contractor_id: '',
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [isDownloadingZip, setIsDownloadingZip] = useState<string | null>(null);
  const [isDownloadingSheet, setIsDownloadingSheet] = useState<string | null>(null);
  const [isReopening, setIsReopening] = useState<string | null>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [users, setUsers] = useState<User[]>([]);
  const [contracts, setContracts] = useState<ContractInfo[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalRows, setTotalRows] = useState<number>(0);

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.employee_name) params.append('employee_name', filters.employee_name);
    if (filters.deputee_id) params.append('deputee_id', filters.deputee_id);
    if (filters.contractor_id) params.append('contract', filters.contractor_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    filters.status.forEach(s => params.append('status', s));
    params.append('page', String(page + 1));
    params.append('page_size', String(pageSize));
    return params;
  }, [filters, page, pageSize]);

  const fetchClaims = useCallback(() => {
    setLoading(true);
    const params = buildFilterParams();
    apiService.getClaimHistory(params)
      .then(response => { if (Array.isArray(response)) { setRows(response as any); setTotalRows((response as any).length); } else { setRows(response.data); setTotalRows(response.total); } })
      .catch((err) => showNotification(err.message || 'Failed to fetch claim history.', 'error'))
      .finally(() => setLoading(false));
  }, [apiService, showNotification, buildFilterParams]);
  
  // --- THIS IS THE FIX ---
  // This useEffect now correctly handles applying the initial filters and triggering a data fetch.
  useEffect(() => {
    const applyInitialFilters = () => {
        if (initialPageFilters) {
            let newFilters = { ...filters };
            if (initialPageFilters.status) {
                newFilters.status = initialPageFilters.status as ClaimSubmissionStatus[];
            }
            setFilters(newFilters);
            setShowFilters(true);
            clearInitialFilters();
        } else {
            fetchClaims();
        }
    };
    applyInitialFilters();
    // This effect should only run once on mount, after initial filters are potentially set.
    // The dependency array is intentionally kept minimal.
  }, [initialPageFilters, clearInitialFilters]); // Removed fetchClaims to prevent loops
  
  // A separate useEffect listens to filter changes to refetch data.
  // This is triggered by both user interaction AND the initial filter application.
  useEffect(() => {
    // We don't want to fetch on the very first render if initial filters are present,
    // as the other useEffect will handle the state update and subsequent fetch.
    if (!initialPageFilters) {
        fetchClaims();
    }
  }, [filters, refreshTrigger, fetchClaims, initialPageFilters]);
  // --- END OF FIX ---


  useEffect(() => {
    apiService.getAllUsers().then(setUsers).catch(console.error);
    apiService.getContracts().then(setContracts).catch(console.error);
  }, [apiService]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, batchId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedBatchId(batchId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name as string]: value })); setPage(0);
  };
  
  const handleDatePreset = (days: number) => {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);
      setFilters(prev => ({
          ...prev,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
      }));
      setPage(0);
  };

  const handleExportCsv = async () => {
    showNotification('Generating CSV export...', 'info');
    try {
        const params = buildFilterParams();
        const blob = await apiService.exportClaimsToCsv(params);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `claim_history_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    } catch (err: any) {
        showNotification(err.message || 'Failed to export CSV.', 'error');
    }
  };

  const handleDownloadZip = async () => {
    if (!selectedBatchId) return;
    setIsDownloadingZip(selectedBatchId);
    const batchIdToDownload = selectedBatchId;
    handleMenuClose();
    try {
      const blob = await apiService.downloadSourceFilesZip(batchIdToDownload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch_${batchIdToDownload}_files.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      showNotification(err.message || 'Failed to download zip file.', 'error');
    } finally {
      setIsDownloadingZip(null);
    }
  };

  const handleDownloadApprovalSheet = async () => {
    if (!selectedBatchId) return;
    setIsDownloadingSheet(selectedBatchId);
    const batchIdToDownload = selectedBatchId;
    const rowData = rows.find(r => r.ingestion_batch_id === batchIdToDownload);
    handleMenuClose();
    try {
      const blob = await apiService.downloadApprovalSheet(batchIdToDownload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = `Approval_Sheet_${rowData?.contractor_id || 'NA'}_${rowData?.employee_name || 'NA'}.xlsx`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      showNotification(err.message || 'Failed to download approval sheet.', 'error');
    } finally {
      setIsDownloadingSheet(null);
    }
  };

  const handleReopenClaim = async () => {
    if (!selectedBatchId) return;
    setIsReopening(selectedBatchId);
    handleMenuClose();
    try {
      await apiService.reopenClaim(selectedBatchId);
      showNotification('Claim successfully re-opened. It is now in the Review Queue.', 'success');
      fetchClaims();
    } catch (err: any) {
      showNotification(err.message || 'Failed to re-open claim.', 'error');
    } finally {
      setIsReopening(null);
    }
  };

  const handleStatusClick = (batchId: string, status: ClaimSubmissionStatus) => {
      if (status === 'error') {
          setErrorBatchId(batchId);
      } else if (status === 'review_required') {
          setReviewingBatchId(batchId);
      } else {
          setReviewingBatchId(batchId);
      }
  };

  const handleViewAuditTrail = () => {
    if (!selectedBatchId) return;
    setAuditBatchId(selectedBatchId);
    handleMenuClose();
  };

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: 'Status',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value.replace(/_/g, ' ')}>
          <IconButton onClick={() => handleStatusClick(params.row.ingestion_batch_id, params.row.status)} size="small">
            {getStatusIcon(params.value)}
          </IconButton>
        </Tooltip>
      )
    },
    {
      field: 'action',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const slaProps = params.row.status === 'review_required' ? getSlaIndicatorProps(params.row.created_at) : null;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {slaProps && (
                    <Tooltip title={slaProps.tooltip}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: slaProps.color, mr: 1 }} />
                    </Tooltip>
                )}
                <Tooltip title="View Details / More Options">
                    <IconButton size="small" onClick={(e) => handleMenuClick(e, params.row.ingestion_batch_id)}>
                        <MoreVertIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        );
      }
    },
    {
      field: 'claim_sequence_id',
      headerName: 'Claim ID',
      width: 120,
      renderCell: (params: GridRenderCellParams) => params.value ? formatClaimId(params.value) : 'N/A',
    },
    {
      field: 'deputee_id',
      headerName: 'Deputee ID',
      width: 120,
      renderCell: (params) => params.value || 'N/A',
    },
    {
      field: 'employee_name',
      headerName: 'Deputee Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || 'N/A',
    },
    {
      field: 'contractor_id',
      headerName: 'Contract Code',
      width: 120,
      renderCell: (params) => params.value || 'N/A',
    },
    {
      field: 'client_name',
      headerName: 'Client Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || 'N/A',
    },
    {
        field: 'claimed_amount',
        headerName: 'Claimed',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => formatCurrency(params.value)
    },
    {
        field: 'approved_amount',
        headerName: 'Approved',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => <Typography color="success.main" sx={{fontWeight: 'bold'}}>{formatCurrency(params.value)}</Typography>
    },
    {
        field: 'rejected_amount',
        headerName: 'Rejected',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => <Typography color="error.main">{formatCurrency(params.value)}</Typography>
    },
    {
      field: 'submitted_by_name',
      headerName: 'Submitted By',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || 'N/A',
    },
    {
      field: 'created_at',
      headerName: 'Submission Date',
      width: 180,
      valueGetter: (params: GridValueGetterParams) => new Date(params.value).toLocaleString(),
    }
  ];

  const canReopen = (status: ClaimSubmissionStatus) => isSuperAdmin() && (status === 'completed' || status === 'partially_approved' || status === 'rejected');
  const selectedRow = selectedBatchId ? rows.find(r => r.ingestion_batch_id === selectedBatchId) : null;
  const canDownloadSheet = selectedRow?.status === 'completed' || selectedRow?.status === 'partially_approved';

  return (
    <Paper
      sx={{
        p: { xs: 2, md: 3 },
        height: 'calc(100vh - 110px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
      elevation={3}
    >
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>Claim History</Typography>
          <Box>
              <Button startIcon={showFilters ? <FilterListOffIcon /> : <FilterListIcon />} onClick={() => setShowFilters(!showFilters)}>Filters</Button>
              <Button startIcon={<DownloadIcon />} onClick={handleExportCsv} sx={{ml: 1}}>Export CSV</Button>
              <Button
                startIcon={<ApprovalSheetIcon />}
                onClick={() => setActivePage('Consolidated Reporting')}
                sx={{ml: 1}}
                variant="contained"
              >
                Generate Approval Sheet
              </Button>
          </Box>
        </Box>
        <Collapse in={showFilters}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Deputee Name" name="employee_name" value={filters.employee_name} onChange={handleFilterChange} variant="outlined" size="small" /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Deputee ID" name="deputee_id" value={filters.deputee_id} onChange={handleFilterChange} variant="outlined" size="small" /></Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        size="small"
                        freeSolo={true}
                        options={[{ id: '', name: 'All' }, ...contracts]}
                        getOptionLabel={(option) => typeof option === 'string' ? option : (option.id === '' ? 'All' : `${option.name} (${option.id})`)}
                        value={contracts.find(c => c.id === filters.contractor_id) || { id: '', name: 'All' }}
                        onChange={(event, newValue) => {
                            if (typeof newValue === 'object' && newValue) {
                                handleFilterChange({
                                    target: { name: 'contractor_id', value: newValue.id || '' }
                                } as any);
                            }
                        }}
                        renderInput={(params) => <TextField {...params} label="Contract" placeholder="Search by name or ID..." />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        multiple
                        value={filters.status}
                        onChange={handleFilterChange as any}
                        input={<OutlinedInput label="Status" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => (
                              <Chip key={value} label={value.replace(/_/g, ' ')} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {allStatuses.map((status) => (
                          <MenuItem key={status} value={status}>
                            <Checkbox checked={filters.status.indexOf(status) > -1} />
                            <ListItemText primary={status.replace(/_/g, ' ')} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}><TextField fullWidth label="Start Date" name="start_date" type="date" value={filters.start_date} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                  <Grid item xs={12} sm={6} md={2}><TextField fullWidth label="End Date" name="end_date" type="date" value={filters.end_date} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                   <Grid item xs={12} sm={12} md={4}>
                       <Box sx={{display: 'flex', gap: 1}}>
                           <Button size="small" variant="outlined" onClick={() => handleDatePreset(7)}>Last 7 Days</Button>
                           <Button size="small" variant="outlined" onClick={() => handleDatePreset(30)}>Last 30 Days</Button>
                       </Box>
                   </Grid>
                  <Grid item xs={12} sm={6} md={2}><Button variant="contained" onClick={() => { setPage(0); fetchClaims(); }} fullWidth>Search</Button></Grid>
                  <Grid item xs={12} sm={6} md={2}><Button variant="outlined" onClick={() => { setFilters({ employee_name: '', status: defaultStatuses, start_date: '', end_date: '', deputee_id: '', contractor_id: '' }); setPage(0); fetchClaims(); }} fullWidth>Clear</Button></Grid>
              </Grid>
          </Paper>
        </Collapse>
      </Box>

      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.ingestion_batch_id}
          loading={loading}
          components={{ NoRowsOverlay: CustomNoRowsOverlay }}
          paginationMode="server"
          rowCount={totalRows}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => { setPageSize(newPageSize); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Box>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={() => { if(selectedBatchId && selectedRow) { handleStatusClick(selectedBatchId, selectedRow.status); } handleMenuClose(); }}>
            <ViewIcon sx={{mr: 1}}/> View Details
        </MenuItem>
        <MenuItem onClick={handleDownloadZip} disabled={!!isDownloadingZip}>
            {isDownloadingZip === selectedBatchId ? <CircularProgress size={20} sx={{ mr: 1.5, ml: 0.5 }} /> : <DownloadIcon sx={{mr: 1}}/>}
            Download Source Files
        </MenuItem>
        <MenuItem onClick={handleViewAuditTrail}>
            <HistoryIcon sx={{mr: 1}}/> View Audit Trail
        </MenuItem>
        {canDownloadSheet && (
            <MenuItem onClick={handleDownloadApprovalSheet} disabled={!!isDownloadingSheet}>
                {isDownloadingSheet === selectedBatchId ? <CircularProgress size={20} sx={{ mr: 1.5, ml: 0.5 }} /> : <ApprovalSheetIcon sx={{mr: 1}}/>}
                Download Approval Sheet
            </MenuItem>
        )}
        {selectedBatchId && rows.find(r => r.ingestion_batch_id === selectedBatchId) && canReopen(rows.find(r => r.ingestion_batch_id === selectedBatchId)?.status as ClaimSubmissionStatus) && (
            <MenuItem onClick={handleReopenClaim} disabled={!!isReopening}>
                {isReopening === selectedBatchId ? <CircularProgress size={20} sx={{ mr: 1.5, ml: 0.5 }} /> : <ReopenIcon sx={{mr: 1}}/>}
                Re-open Claim
            </MenuItem>
        )}
      </Menu>
    </Paper>
  );
}

